/*!

=========================================================
* Paper Dashboard PRO React - v1.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import AdminLayout from './layouts/Admin.js';
import SplashLayout from './layouts/Splash.js';
import Fraud from './layouts/Fraud';
import AdverseLayout from './layouts/Adverse.js';
import MultipleLoans from './layouts/MultipleLoans.js';

import 'bootstrap/dist/css/bootstrap.css';
import './assets/scss/paper-dashboard.scss';
import './assets/demo/demo.css';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import '../src/assets/css/root.css';
import { useAuth0 } from '@auth0/auth0-react';
import FcRoutes from './services/eventService';
import LoadingMsg from './components/LoadingMsg';
import ErrorScreen from './views/components/Error-Screen';

// Utils
import { auxShowErrorScreen } from './utils';
import SocialMediaIcons from './views/components/SocialMediaIcons';
import { trackPageView, updateNewRelicUser, setCustomNewRelicAttribute } from './config/newrelic';

// Route Tracker Component for New Relic
const RouteTracker = () => {
  const location = useLocation();
  const TemplateConfig = useSelector((state) => state.TemplateConfig);
  const isNewRelicEnabled = Boolean(TemplateConfig?.pages?.features?.newRelic);
  const silentTrackingNewRelic =
    (TemplateConfig?.pages?.features?.newRelic || {})?.silentTracking ??
    (TemplateConfig?.pages?.features?.fullStory || {})?.silentTracking ??
    true; 
  const consent = JSON.parse(localStorage.getItem('newRelicConsent'));
  const consentGiven = consent === true;

  useEffect(() => {
    if (isNewRelicEnabled && (consentGiven || silentTrackingNewRelic)) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, isNewRelicEnabled, consentGiven, silentTrackingNewRelic]);

  return null;
};

// 10/16/2025 3:42 pm
const App = () => {
  const [token, setToken] = useState(null);
  const [myUser, setMyUser] = useState(null);
  const [loanFile, setLoanFile] = useState(null);
  const [loanFileErr, setLoanFileErr] = useState(false);
  const [isFraud, setIsFraud] = useState(false);
  const [identityItem, setIdentityItem] = useState(null);
  const [identityVerificationId, setIdentityVerificationId] = useState(null);
  const [identityError, setIdentityError] = useState(false);
  const [myLoading, setMyLoading] = useState(true);
  const [closingLink] = useState(null);
  const [roomInfo] = useState(null);
  const [disclosuresSpinner, setDisclosuresSpinner] = useState(false);
  const [messageCount, setMessageCount] = useState(null);
  const [messages] = useState([]);
  const [loanTeam] = useState([]);
  const [loadingChat] = useState(false);
  const [hasAllIncompleteLoans, setHasAllIncompleteLoans] = useState(false);
  const TemplateConfig = useSelector((state) => state.TemplateConfig);

  let apiClient = null;

  const { isAuthenticated, isLoading, error, getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const getAccessToken = async () => {
    try {
      const token = await getAccessTokenSilently();
      return token;
    } catch (error) {
      if (error.message === 'Login required') {
        loginWithRedirect();
      }
      return null;
    }
  };

  const savedLoanCard = JSON.parse(localStorage.getItem('selectedLoan'));
  const loanId = savedLoanCard?.loanId ?? loanFile?.[0]?.loanId;

  useEffect(() => {
    let isMounted = true;

    const initializeApp = async () => {
      if (!isAuthenticated || isLoading) {
        return;
      }

      try {
        const newToken = await getAccessToken();
        if (!newToken) {
          return;
        }

        setToken(newToken);

        if (newToken) {
          apiClient = new FcRoutes(newToken);

          if (!myUser) {
            try {
              const res = await apiClient.getLoans();

              if (isMounted) {
                if (res.data == null || res?.data?.length === 0) {
                  setHasAllIncompleteLoans(true);
                }
                setMyUser(res.data);
                setIdentityVerificationId(res.data?.user_metadata?.eventId);
              }
            } catch (err) {
              setMyLoading(false);
              setLoanFileErr(true);
            }
          } else if (!loanFile) {
            try {
              const res = await apiClient.getLoans();

              if (isMounted) {
                setLoanFile(res.data);
                const borrowers = res?.data?.[0]?.borrowers || [];
                const userEmail = user?.email || myUser?.email;
                const matchedBorrower = borrowers.find(
                  (b) => (b.emailAddress || '').toLowerCase() === (userEmail || '').toLowerCase()
                );
                const selectedBorrower = matchedBorrower || borrowers[0];
                const { firstName, lastName, birthDate, phoneNumber } = selectedBorrower || {};
                const idItem = {
                  fileId: loanId,
                  borrower: {
                    borrowerFirstName: firstName,
                    borrowerLastName: lastName,
                    borrowerDOB: birthDate,
                    borrowerPrimaryPhone: phoneNumber,
                  },
                  borrowerProperty: {
                    propertyStreetAddress: res?.data?.[0].property?.streetAddress,
                    propertyCity: res?.data?.[0].property?.city,
                    propertyState: res?.data?.[0].property?.state,
                    propertyCounty: res?.data?.[0].property?.county,
                    propertyZip: res?.data?.[0].property?.postalCode,
                  },
                  previousReferenceId: loanFile?.user_metadata?.previousReferenceId,
                };

                if (phoneNumber) {
                  setIdentityItem(idItem);
                } else {
                  setIdentityError(true);
                }
                setMyLoading(false);

                if (res.data == null || res?.data?.length === 0) {
                  setHasAllIncompleteLoans(true);
                }

                const emails = res.data?.[0].borrowers?.map((x) => x.borrowerEmailAddress);
                const addSigningRoomRequestItem = emails?.length > 0 ? { loanId, type: 'Initial', emails } : { loanId, type: 'Initial' };

                if (loanFile?.app_metadata?.roomInfo) {
                  setDisclosuresSpinner(true);
                }
              }
            } catch (err) {
              setLoanFileErr(true);
              setMyLoading(false);
            }
          }
        }
      } catch (error) {
        setMyLoading(false);
        setLoanFileErr(true);
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading, token, myUser]);


  useEffect(() => {
    const isNewRelicEnabled = Boolean(TemplateConfig?.pages?.features?.newRelic);
    const silentTrackingNewRelic =
      (TemplateConfig?.pages?.features?.newRelic || {})?.silentTracking ??
      (TemplateConfig?.pages?.features?.fullStory || {})?.silentTracking ??
      true;
    const consent = JSON.parse(localStorage.getItem('newRelicConsent'));
    const consentGiven = consent === true;

    if (isNewRelicEnabled && (consentGiven || silentTrackingNewRelic)) {
      const userEmail = user?.email || myUser?.email || myUser?.user_metadata?.email;
      if (userEmail) {
        updateNewRelicUser(userEmail);
      }

      if (myUser?.app_metadata?.loanNumber) {
        setCustomNewRelicAttribute('loanNumber', myUser.app_metadata.loanNumber);
      } else if (loanFile?.[0]?.loanNumber) {
        setCustomNewRelicAttribute('loanNumber', loanFile[0].loanNumber);
      }

      const borrowers = loanFile?.[0]?.borrowers || [];
      const borrowerType = myUser?.app_metadata?.borrowerType || myUser?.user_metadata?.borrowerType;
      
      if (borrowers.length > 0 && borrowerType) {
        const borrower = borrowers.find(
          (b) =>
            (!b.isPrimary && borrowerType === 'COBORROWER') ||
            (b.isPrimary && borrowerType === 'BORROWER'),
        );
        
        if (borrower) {
          if (borrower.borrowerPhoneNumber || borrower.phoneNumber) {
            setCustomNewRelicAttribute('phone', borrower.borrowerPhoneNumber || borrower.phoneNumber);
          }
          if (borrower.borrowerFirstName && borrower.borrowerLastName) {
            setCustomNewRelicAttribute('fullName', `${borrower.borrowerFirstName} ${borrower.borrowerLastName}`);
          }
        }
      } else if (borrowers.length > 0) {
        const borrower = borrowers[0];
        if (borrower.borrowerPhoneNumber || borrower.phoneNumber) {
          setCustomNewRelicAttribute('phone', borrower.borrowerPhoneNumber || borrower.phoneNumber);
        }
        if (borrower.borrowerFirstName && borrower.borrowerLastName) {
          setCustomNewRelicAttribute('fullName', `${borrower.borrowerFirstName} ${borrower.borrowerLastName}`);
        }
      }
    }
  }, [user, myUser, loanFile, TemplateConfig]);

  if (isLoading) {
    return <LoadingMsg type='Splash' message='Hold on while we authenticate your login.' />;
  }

  if (error) {
    return <div>Error: {error.message} Please contact support.</div>;
  }

  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }

  if (loanFile === null && !isLoading && isAuthenticated && !loanFileErr && !hasAllIncompleteLoans) {
    return <LoadingMsg type='Splash' message='Getting loan file.' />;
  } else if (hasAllIncompleteLoans === true) {
    return <LoadingMsg type='NoEligibleLoans' message={TemplateConfig?.errorMessages?.incompleteLoansError?.body} />;
  }

  const determineRoutes = () => {
    if (auxShowErrorScreen) {
      return (
        <div className='error_screen_container'>
          <div className='error_screen_bar'>
            <div className='error_screen_logo_container'>
              <img src='logo' alt='logo' className='logo_error_screen' />
            </div>
          </div>
          <div className='error_screen_content'>
            <div className='error_screen_title'>What happened?...</div>
            <div className='error_screen_body'>
              Please email{' '}
              <a className='error_screen_email' href={`mailto:${adminNavbar?.emailContact}`} target='_blank'>
                {adminNavbar?.emailContact}
              </a>{' '}
              or
              <br />
              call {adminNavbar?.phoneContact} for assistance
            </div>
            <a href={adminNavbar?.returnLink} className='error_screen_button'>
              {adminNavbar?.returnLinkText}
            </a>
          </div>
          <SocialMediaIcons />
        </div>
      );
    }
    //It"s important that fraud is checked for first, as there could be adverse action on the loan as well, so need to catch fraud upfront.
    if (loanFile?.loanStatus?.statusName == 'FRAUD' || isFraud) {
      return (
        <>
          <Route
            path='/contact'
            render={(props) => (
              <Fraud
                {...props}
                user={myUser}
                loanFile={loanFile}
                loanFileErr={loanFileErr}
                idItem={identityItem}
                idErr={identityError}
                identityVerificationId={identityVerificationId}
                setIdentityVerificationId={setIdentityVerificationId}
                token={token}
              />
            )}
          />
          <Redirect to='/contact' />
        </>
      );
    } else if (loanFileErr) {
      return <ErrorScreen />;
    } else if (loanFile?.loanStatus?.hasAdverseAction || myUser?.app_metadata?.isAdverse) {
      return (
        <>
          {loanFileErr ? (
            <></>
          ) : (
            <>
              <Route
                path='/adverse'
                render={(props) => (
                  <AdverseLayout
                    {...props}
                    loading={myLoading}
                    user={myUser}
                    loanFile={loanFile}
                    loanFileErr={loanFileErr}
                    idItem={identityItem}
                    idErr={identityError}
                    closingLink={closingLink}
                    token={token}
                  />
                )}
              />
              <Redirect to='/adverse' />
            </>
          )}
        </>
      );
    } else {
      return (
        <>
          <Route
            exact
            path='/splash'
            render={(props) => (
              <SplashLayout
                {...props}
                loading={myLoading}
                user={myUser}
                loanFile={loanFile}
                loanFileErr={loanFileErr}
                idItem={identityItem}
                idErr={identityError}
                closingLink={closingLink}
                loanId={loanId}
                //setHasVisitedSplashPage={setHasVisitedSplashPage}
              />
            )}
          />
          <Route
            exact
            path='/multiple/loans'
            render={(props) => <MultipleLoans {...props} user={myUser} loanFile={loanFile} loanFileErr={loanFileErr} />}
          />
          <Route
            path='/admin'
            render={(props) => (
              <AdminLayout
                {...props}
                loading={myLoading}
                user={myUser}
                loanFile={loanFile}
                loanFileErr={loanFileErr}
                setIdentityVerificationId={setIdentityVerificationId}
                identityVerificationId={identityVerificationId}
                setIsFraud={setIsFraud}
                idItem={identityItem}
                idErr={identityError}
                closingLink={closingLink}
                token={token}
                roomInfo={roomInfo}
                disclosuresSpinner={disclosuresSpinner}
                messages={messages}
                messageCount={messageCount}
                loanTeam={loanTeam}
                loadingChat={loadingChat}
                setMessageCount={setMessageCount}
              />
            )}
          />

          {<>{loanFile.length > 1 && !savedLoanCard ? <Redirect to='/multiple/loans' /> : <Redirect to='/admin/loan-info' />}</>}
        </>
      );
    }
  };

  return (
    <>
      {loanFileErr ? (
        <ErrorScreen />
      ) : (
        <BrowserRouter basename={`${process.env.REACT_APP_HOMEPAGE_PATH}`}>
          <RouteTracker />
          <Switch>{isAuthenticated ? determineRoutes() : <Redirect to={loginWithRedirect()} from='*' />}</Switch>
        </BrowserRouter>
      )}
    </>
  );
};

export default App;
